import React, { useEffect } from 'react';


/**
 * A React Hook for holding toggle state for a set amount of time.
 * @param startToggled - whether or not the hook should initialize as toggled. Default: `false`
 * @param delay - the duration in milliseconds for which the state should remain toggled. Default: `15000`
 * @returns Array where first item is the toggle state, and the second item is a function that flips the toggle state
 *          when called
 */
export const useTimedToggle = (
  startToggled: boolean | (() => boolean) = false,
  delay: number                           = 15000,
): [boolean, () => void] => {
  const [isToggled, setIsToggled] = React.useState(startToggled);

  useEffect(
    () => {
      if (isToggled !== startToggled) {
        const handle = setTimeout(() => setIsToggled(!isToggled), delay);
        // Return a function to React that it can call when the component is unmounting to clean up this effect
        return () => clearTimeout(handle);
      }
    },
    [delay, isToggled, startToggled],
  );

  return [isToggled, () => setIsToggled(!isToggled)];
};
